import * as Dialog from '@radix-ui/react-dialog';
import type { TCountryCode } from 'countries-list';
import { getCountryData } from 'countries-list';
import { Button, Select } from 'hem-ui';
import Cookies from 'js-cookie';
import React, { useEffect, useState } from 'react';

import {
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
} from '@/components/ui/dialog';
import { useIPLocation } from '@/hooks/useIPLocation';

import { IS_PRO, REGION } from '../constants';

const storeOptions = [
  ...(IS_PRO ? [] : [{ value: 'CA', label: 'Canada ($ CAD)' }]),
  { value: 'EU', label: 'Europe (€ EUR)' },
  { value: 'GB', label: 'United Kingdom (£ GBP)' },
  { value: 'US', label: 'United States ($ USD)' },
];

const currentLocale = REGION === 'uk' ? 'gb' : REGION;

export const LocaleRedirect = () => {
  const [selectValue, setSelectValue] = useState<string>('');
  const [regionModalOpen, setRegionModalOpen] = useState(false);
  const { data: ipData, status: ipStatus } = useIPLocation();

  useEffect(() => {
    const regionCookie = Cookies.get('hem-region-default');
    if (regionCookie) {
      // If the user has a saved preference, don't show the dialog
      return;
    }

    // Check IP location if user doesn't have a saved preference
    // If they have a saved preference, they should already be redirected by gatsby-ssr.js
    if (ipStatus === 'success' && ipData) {
      const { country, continent } = ipData;

      const selectValue =
        continent === 'EU' && country !== 'GB' ? 'EU' : country;

      // Suggest the store based on the user's location
      if (storeOptions.some((o) => o.value === selectValue)) {
        setSelectValue(selectValue);
      }

      // Open the dialog if the locales don't match
      if (selectValue?.toLowerCase() !== currentLocale) {
        setRegionModalOpen(true);
      }
    }
  }, [ipData, ipStatus]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const value = params.get('locale');
    if (value) {
      // Save the user's preference
      Cookies.set('hem-region-default', value, { expires: 365 });

      // Remove the locale query parameter
      params.delete('locale');
      const paramString = params.toString();
      const newUrl =
        window.location.pathname + (paramString ? '?' + paramString : '');
      window.history.replaceState({}, '', newUrl);
    }
  }, []);

  const { name: countryName, continent: continentName } =
    getCountryData(ipData?.country as TCountryCode) || {};

  const suggestedStore = storeOptions.find(
    (option) => option.value === countryName || option.value === continentName,
  );

  return (
    <Dialog.Root open={regionModalOpen} onOpenChange={setRegionModalOpen}>
      <Dialog.Portal>
        <DialogOverlay />
        <DialogContent className="max-w-screen-sm">
          <DialogHeader>
            <DialogTitle>
              {countryName
                ? `Are you browsing from ${countryName}?`
                : 'Select your country preference'}
            </DialogTitle>
          </DialogHeader>
          <DialogDescription className="text-pretty p-4 pb-0">
            {countryName ? (
              <>
                This store does not ship to {countryName}.{' '}
                {suggestedStore?.label ? (
                  <>
                    Would you like to visit our{' '}
                    {suggestedStore.label.replace(/\s*\(.*?\)\s*/g, '')} store
                    instead?
                  </>
                ) : null}
              </>
            ) : (
              <>
                Please update your country preference to see content specific to
                your location.
              </>
            )}
          </DialogDescription>

          <div className="space-y-2 p-4">
            <Select
              label="Country preference"
              value={selectValue}
              onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                const value = event.target.value;
                setSelectValue(value);
              }}
            >
              <option value="">Select country preferences</option>
              {storeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
            <Button
              className="w-full"
              disabled={selectValue === ''}
              color={selectValue === '' ? 'disabled' : 'blue'}
              onClick={() => {
                // Save the user's preference
                const value = selectValue.toLowerCase();
                Cookies.set('hem-region-default', value, { expires: 365 });

                // Check if the current locale is the same as the new locale
                if (currentLocale !== value) {
                  // Redirect to the new locale
                  const newUrl = window.location.href.replace(
                    /en-\w{2}/,
                    `en-${value}`,
                  );

                  window.location.href = newUrl;
                } else {
                  // Close the dialog if the locale is the same
                  setRegionModalOpen(false);
                }
              }}
            >
              Update preference
            </Button>
            <Button
              className="w-full"
              disabled={selectValue === ''}
              color="white-with-blue-border"
              onClick={() => {
                Cookies.set('hem-region-default', currentLocale, {
                  expires: 365,
                });

                setRegionModalOpen(false);
              }}
            >
              Stay on current site
            </Button>
          </div>

          <DialogClose />
        </DialogContent>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
